import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Building2, Plus, ChevronLeft, MapPin, Shield, Star,
  Bell, Wrench, Vote, Megaphone, Activity, BellOff, LogOut, AlertTriangle,
} from 'lucide-react';
import { toast } from 'sonner';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../components/ui/alert-dialog';

const API = process.env.REACT_APP_BACKEND_URL;
const authHeaders = () => {
  const t = localStorage.getItem('session_token');
  return t ? { Authorization: `Bearer ${t}` } : {};
};

const ROLE_LABEL = {
  resident: 'Resident', trustee: 'Trustee', chairman: 'Chairman',
  property_manager: 'Property Manager', managing_agent: 'Managing Agent',
  agent_admin: 'Agent Admin', super_agent: 'Super Agent',
};

const ROLE_COLOUR = {
  resident: 'bg-slate-100 text-slate-700',
  trustee: 'bg-blue-100 text-blue-800',
  chairman: 'bg-purple-100 text-purple-800',
  property_manager: 'bg-emerald-100 text-emerald-800',
  managing_agent: 'bg-indigo-100 text-indigo-800',
};

export default function MyBuildings() {
  const { myBuildings, activeBuilding, switchBuilding, refreshMyBuildings, user } = useAuth();
  const navigate = useNavigate();
  const [activity, setActivity] = useState([]);
  const [activityLoading, setActivityLoading] = useState(true);
  const [leaving, setLeaving] = useState(null);  // { hoa_id, building_name }
  const [leaveSubmitting, setLeaveSubmitting] = useState(false);

  const leaveBuilding = async () => {
    if (!leaving) return;
    setLeaveSubmitting(true);
    try {
      const r = await fetch(`${API}/api/me/buildings/${leaving.hoa_id}`, {
        method: 'DELETE',
        credentials: 'include',
        headers: authHeaders(),
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) {
        const detail = data.detail;
        if (detail && typeof detail === 'object' && detail.code === 'primary_admin_must_transfer') {
          toast.error("You're the Primary Admin — transfer the role to another admin before leaving.");
        } else if (detail && typeof detail === 'object' && detail.error === 'continuity_violation') {
          toast.error('Cannot leave: you are the last active admin. Add another admin first.');
        } else {
          toast.error(typeof detail === 'string' ? detail : 'Could not leave building');
        }
        return;
      }
      toast.success(data.message || 'Left building');
      setLeaving(null);
      await refreshMyBuildings();
      fetchActivity();
    } catch (e) {
      toast.error('Network error');
    } finally {
      setLeaveSubmitting(false);
    }
  };

  const fetchActivity = useCallback(async () => {
    setActivityLoading(true);
    const r = await fetch(`${API}/api/me/recent-activity?limit=15`, {
      credentials: 'include', headers: authHeaders(),
    });
    if (r.ok) {
      const d = await r.json();
      setActivity(d.activity || []);
    }
    setActivityLoading(false);
  }, []);

  useEffect(() => { refreshMyBuildings(); fetchActivity(); }, [refreshMyBuildings, fetchActivity]);

  const toggleMute = async (b, e) => {
    e.stopPropagation();
    const next = !b.is_muted;
    const r = await fetch(`${API}/api/me/buildings/${b.hoa_id}/mute`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json', ...authHeaders() },
      body: JSON.stringify({ muted: next }),
    });
    if (r.ok) {
      toast.success(next ? `Muted ${b.building_name}` : `Unmuted ${b.building_name}`);
      await refreshMyBuildings();
      fetchActivity();
    } else {
      toast.error('Could not update mute state');
    }
  };

  const openBuilding = async (b) => {
    if (b.hoa_id !== activeBuilding?.hoa_id) {
      const ok = await switchBuilding(b.hoa_id);
      if (!ok) return toast.error('Could not switch');
    }
    // Navigate to the building dashboard based on role
    if (['platform_owner'].includes(user?.role)) navigate(`/platform/buildings/${b.hoa_id}`);
    else if (b.is_primary_admin || ['trustee', 'chairman', 'property_manager'].includes(b.role)) navigate('/admin');
    else if (['managing_agent', 'agent_admin', 'super_agent', 'general_agent'].includes(b.role)) navigate('/agent');
    else navigate('/dashboard');
  };

  return (
    <div className="min-h-screen bg-gray-50 p-4 sm:p-6 lg:p-8" data-testid="my-buildings-page">
      <div className="max-w-5xl mx-auto">
        <div className="mb-6 flex items-center gap-3">
          <button onClick={() => navigate(-1)} className="p-2 rounded hover:bg-gray-200">
            <ChevronLeft className="w-5 h-5" />
          </button>
          <div className="flex-1">
            <h1 className="text-2xl sm:text-3xl font-bold flex items-center gap-2">
              <Building2 className="w-7 h-7 text-indigo-600" /> My Buildings
            </h1>
            <p className="text-gray-600 text-sm">
              All buildings linked to your BodyCorp365 account · One login, multiple properties.
            </p>
          </div>
          <Button onClick={() => navigate('/find-building')} data-testid="add-building-cta">
            <Plus className="w-4 h-4 mr-1" /> Add Building
          </Button>
        </div>

        {myBuildings.length === 0 ? (
          <Card>
            <CardContent className="p-10 text-center">
              <Building2 className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <h2 className="text-xl font-semibold mb-2">No buildings yet</h2>
              <p className="text-gray-600 mb-4">
                Link your first building to get started — find an existing one or create a new building profile.
              </p>
              <Button onClick={() => navigate('/find-building')} data-testid="empty-add-building">
                <Plus className="w-4 h-4 mr-1" /> Add your first building
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {myBuildings.map((b) => {
              const isActive = b.hoa_id === activeBuilding?.hoa_id;
              return (
                <Card
                  key={b.hoa_id}
                  className={`relative transition-shadow hover:shadow-md cursor-pointer ${isActive ? 'ring-2 ring-indigo-300' : ''}`}
                  onClick={() => openBuilding(b)}
                  data-testid={`building-card-${b.hoa_id}`}
                >
                  <CardContent className="p-5">
                    {isActive && (
                      <Badge variant="outline" className="absolute top-3 right-3 bg-indigo-50 text-indigo-700 border-indigo-200">
                        Active
                      </Badge>
                    )}
                    {b.is_muted && !isActive && (
                      <Badge variant="outline" className="absolute top-3 right-3 bg-gray-200 text-gray-700 border-gray-300 flex items-center gap-1">
                        <BellOff className="w-3 h-3" /> Muted
                      </Badge>
                    )}
                    <div className="flex items-start gap-3 mb-3">
                      <div className="p-2 bg-indigo-50 rounded-lg">
                        <Building2 className="w-6 h-6 text-indigo-600" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="font-bold text-lg truncate">{b.building_name}</h3>
                        <div className="text-xs text-gray-500 flex items-center gap-1 mt-0.5">
                          {b.building_type && <span>{b.building_type}</span>}
                          {b.city && <><span>·</span><MapPin className="w-3 h-3" /><span>{b.city}</span></>}
                        </div>
                      </div>
                    </div>

                    <div className="flex flex-wrap items-center gap-2 mb-3">
                      <Badge variant="outline" className={ROLE_COLOUR[b.role] || 'bg-slate-100 text-slate-700'}>
                        {ROLE_LABEL[b.role] || b.role}
                      </Badge>
                      {b.unit_number && (
                        <Badge variant="outline" className="bg-white">Unit {b.unit_number}</Badge>
                      )}
                      {b.is_primary_admin && (
                        <Badge variant="outline" className="bg-amber-50 text-amber-800 border-amber-200">
                          <Star className="w-3 h-3 mr-1 fill-current" /> Primary Admin
                        </Badge>
                      )}
                      {b.admin_level && b.admin_level !== 'primary_admin' && (
                        <Badge variant="outline" className="bg-purple-50 text-purple-700">
                          <Shield className="w-3 h-3 mr-1" /> {b.admin_level.replace('_', ' ')}
                        </Badge>
                      )}
                    </div>

                    <div className="flex items-center gap-2">
                      <Button
                        variant={isActive ? 'default' : 'outline'}
                        size="sm"
                        className="flex-1"
                        onClick={(e) => { e.stopPropagation(); openBuilding(b); }}
                        data-testid={`open-${b.hoa_id}`}
                      >
                        {isActive ? 'Open Dashboard' : 'Switch & Open'}
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={(e) => toggleMute(b, e)}
                        title={b.is_muted ? 'Unmute updates' : 'Mute updates'}
                        data-testid={`mute-${b.hoa_id}`}
                      >
                        {b.is_muted ? <BellOff className="w-4 h-4 text-gray-500" /> : <Bell className="w-4 h-4" />}
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={(e) => { e.stopPropagation(); setLeaving({ hoa_id: b.hoa_id, building_name: b.building_name, is_primary_admin: b.is_primary_admin }); }}
                        title="Remove this building from my list"
                        className="text-red-600 hover:bg-red-50 hover:text-red-700"
                        data-testid={`leave-${b.hoa_id}`}
                      >
                        <LogOut className="w-4 h-4" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

        {/* Recent Activity feed across all buildings */}
        {myBuildings.length > 0 && (
          <Card className="mt-6" data-testid="recent-activity-card">
            <CardContent className="p-5">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold flex items-center gap-2">
                  <Activity className="w-5 h-5 text-indigo-600" />
                  Recent Activity Across All Your Buildings
                </h3>
                <Button variant="ghost" size="sm" onClick={fetchActivity} data-testid="refresh-activity">
                  Refresh
                </Button>
              </div>
              {activityLoading ? (
                <div className="text-center py-6 text-sm text-gray-500">Loading…</div>
              ) : activity.length === 0 ? (
                <div className="text-center py-6 text-sm text-gray-500">
                  No recent activity in any of your linked buildings.
                </div>
              ) : (
                <div className="divide-y">
                  {activity.map((item) => {
                    const Icon =
                      item.type === 'notice' ? Bell
                      : item.type === 'ticket' ? Wrench
                      : item.type === 'vote' ? Vote
                      : item.type === 'communication' ? Megaphone
                      : Activity;
                    const iconBg =
                      item.type === 'notice' ? 'bg-blue-100 text-blue-700'
                      : item.type === 'ticket' ? 'bg-amber-100 text-amber-700'
                      : item.type === 'vote' ? 'bg-purple-100 text-purple-700'
                      : item.type === 'communication' ? 'bg-emerald-100 text-emerald-700'
                      : 'bg-gray-100 text-gray-700';
                    return (
                      <button
                        key={`${item.type}-${item.id}-${item.created_at}`}
                        onClick={async () => {
                          if (item.hoa_id && item.hoa_id !== activeBuilding?.hoa_id) {
                            await switchBuilding(item.hoa_id);
                          }
                          if (item.type === 'ticket') navigate('/dashboard/maintenance');
                          else if (item.type === 'vote') navigate('/voting');
                          else if (item.type === 'notice') navigate('/notices');
                          else navigate('/notifications');
                        }}
                        className="w-full py-3 flex items-start gap-3 text-left hover:bg-gray-50 px-2 -mx-2 rounded"
                        data-testid={`activity-${item.type}-${item.id}`}
                      >
                        <div className={`p-2 rounded-full shrink-0 ${iconBg}`}>
                          <Icon className="w-4 h-4" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-medium truncate">{item.title}</span>
                            <Badge variant="outline" className="capitalize text-xs">{item.type}</Badge>
                            {item.building_name && (
                              <Badge variant="outline" className="bg-indigo-50 text-indigo-700 border-indigo-200 text-xs">
                                {item.building_name}
                              </Badge>
                            )}
                          </div>
                          {item.body && (
                            <div className="text-xs text-gray-600 mt-0.5 line-clamp-2">{item.body}</div>
                          )}
                          <div className="text-xs text-gray-400 mt-1">
                            {new Date(item.created_at).toLocaleString()}
                            {item.actor && ` · by ${item.actor}`}
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </div>

      {/* Leave Building Confirmation */}
      <AlertDialog open={!!leaving} onOpenChange={(o) => !o && setLeaving(null)}>
        <AlertDialogContent data-testid="leave-building-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-500" />
              Remove this building from your list?
            </AlertDialogTitle>
            <AlertDialogDescription>
              You'll be unlinked from <strong>{leaving?.building_name}</strong>. Your account stays active and the building, its residents, and all data will remain intact for other members. You can re-join later if invited.
              {leaving?.is_primary_admin && (
                <span className="block mt-3 p-2 bg-red-50 text-red-700 rounded text-sm">
                  ⚠️ You are the Primary Admin. Transfer the role to another admin first or this will be blocked.
                </span>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="leave-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={leaveBuilding}
              disabled={leaveSubmitting}
              className="bg-red-600 hover:bg-red-700"
              data-testid="leave-confirm"
            >
              {leaveSubmitting ? 'Leaving…' : 'Leave Building'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
